import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Divider,
  Toolbar,
} from "@mui/material";
import Api, { APIs } from "../../../Api";

export type ADPProject = {
  sr_no: number | null;
  adp_no: string | null;
  sector: string | null;
  approval_date: string | null;
  approval_authority: string | null;
  approved_project_cost_usd_m: number | null;
  "adp_2023-24_allocation_usd_m": number | null;
  "adp_2024-25_allocation_usd_m": number | null;
  "adp_2025-26_allocation_usd_m": number | null;
  project_title: string | null;
};

const DOLLAR_RATE = 280;

let DOLLAR_RATE_FOR_APPROVED = 220;

let DOLLAR_RATE_2023_24 = 220;

let DOLLAR_RATE_2024_25 = 220;

let DOLLAR_RATE_2025_26 = DOLLAR_RATE;

interface ADPProjectsTableProps {
  province: string;
}

const fmt = (n: number | string | null | undefined) =>
  n === null || n === undefined || isNaN(+n)
    ? "–"
    : (+n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function ADPProjectsTable({ province }: ADPProjectsTableProps) {
  const [rows, setRows] = useState<ADPProject[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sum = (key: keyof ADPProject) =>
    rows?.reduce((acc, r) => {
      const val = r[key];
      const num = typeof val === "number" ? val : parseFloat(val as any) || 0;
      return acc + num;
    }, 0);

  const displayRows = useMemo(() => {
    if (!rows) return [];

    return [...rows].sort((a, b) => {
      const aNo = a.sr_no ?? Number.MAX_SAFE_INTEGER;
      const bNo = b.sr_no ?? Number.MAX_SAFE_INTEGER;

      return aNo - bNo; // Ascending order
    });
  }, [rows]);

  useEffect(() => {
    if (!province) return;

    setLoading(true);
    Api.get(
      APIs.MoPD_ADP_PROJECTS,
      {},
      { province: province === "ajk" ? "kashmir" : province },
    )
      .then((payload) => setRows(payload))
      .catch((e: any) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [province]);

  if (!province) return null;

  const totals = {
    approved:
      rows?.reduce((acc, r) => acc + (r.approved_project_cost_usd_m ?? 0), 0) ??
      0,
    a2324:
      rows?.reduce(
        (acc, r) => acc + (r["adp_2023-24_allocation_usd_m"] ?? 0),
        0,
      ) ?? 0,
    a2425:
      rows?.reduce(
        (acc, r) => acc + (r["adp_2024-25_allocation_usd_m"] ?? 0),
        0,
      ) ?? 0,
    a2526: sum("adp_2025-26_allocation_usd_m") ?? 0,
    pkr_to_usd: {
      approved:
        (rows?.reduce(
          (acc, r) => acc + (r.approved_project_cost_usd_m ?? 0),
          0,
        ) ?? 0) / DOLLAR_RATE_FOR_APPROVED,
      a2324:
        (rows?.reduce(
          (acc, r) => acc + (r["adp_2023-24_allocation_usd_m"] ?? 0),
          0,
        ) ?? 0) / DOLLAR_RATE_2023_24,
      a2425:
        (rows?.reduce(
          (acc, r) => acc + (r["adp_2024-25_allocation_usd_m"] ?? 0),
          0,
        ) ?? 0) / DOLLAR_RATE_2024_25,
      a2526: (sum("adp_2025-26_allocation_usd_m") ?? 0) / DOLLAR_RATE_2025_26,
    },
  };

  return (
    <Paper
      sx={{
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        p: 2,
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1, justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight={700}>
          Province:{" "}
          {province === "ajk"
            ? "AJK"
            : province.charAt(0).toUpperCase() + province.slice(1)}
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
          US$ Million
        </Typography>
      </Toolbar>
      <Divider sx={{ mb: 2 }} />
      {loading && <LinearProgress />}
      {error && <Box sx={{ color: "error.main" }}>{error}</Box>}
      <TableContainer
        sx={{
          flex: 1,
          overflow: "auto",
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Sr. No.</TableCell>
              <TableCell sx={th}>ADP #</TableCell>
              <TableCell sx={thCenter}>Sector</TableCell>
              <TableCell sx={thCenter}>Project Title</TableCell>
              <TableCell sx={thCenter}>Approval Authority</TableCell>
              <TableCell sx={thCenter}>Approval Date</TableCell>
              <TableCell sx={thRight}>
                Approved Project Cost (PKR. Mn)
              </TableCell>
              <TableCell sx={thRight}>
                ADP 2023-24 Allocation (PKR. Mn)
              </TableCell>
              <TableCell sx={thRight}>
                ADP 2024-25 Allocation (PKR. Mn)
              </TableCell>
              <TableCell sx={thRight}>
                ADP 2025-26 Allocation (PKR. Mn)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows?.map((r, idx) => (
              <TableRow key={r.sr_no ?? idx} hover>
                <TableCell>{r.sr_no ?? idx + 1}</TableCell>
                <TableCell>{r.adp_no ?? "–"}</TableCell>
                <TableCell>{r.sector ?? "–"}</TableCell>
                <TableCell>{r.project_title ?? "–"}</TableCell>
                <TableCell>{r.approval_authority}</TableCell>
                <TableCell>{r.approval_date ?? "–"}</TableCell>
                <TableCell align="right">
                  {fmt(r.approved_project_cost_usd_m)}
                </TableCell>
                <TableCell align="right">
                  {fmt(r["adp_2023-24_allocation_usd_m"])}
                </TableCell>
                <TableCell align="right">
                  {fmt(r["adp_2024-25_allocation_usd_m"])}
                </TableCell>
                <TableCell align="right">
                  {fmt(r["adp_2025-26_allocation_usd_m"])}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={totalCell} colSpan={6}>
                Total PKR Million
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.approved)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.a2324)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.a2425)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.a2526)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={totalCell} colSpan={6}>
                Total US$ Million
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.pkr_to_usd.approved)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.pkr_to_usd.a2324)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.pkr_to_usd.a2425)}
              </TableCell>
              <TableCell sx={totalNum} align="right">
                {fmt(totals.pkr_to_usd.a2526)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {/* Footer fixed below */}
      <Box
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Updated on: June 27, 2025
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Source: RRU / MoPD&SI
        </Typography>
      </Box>
    </Paper>
  );
}

// ---- Cell styles (MUI SX) ----
const base = {
  borderRight: (t: any) => `1px solid ${t.palette.divider}`,
  borderBottom: (t: any) => `1px solid ${t.palette.divider}`,
};
const th = {
  ...base,
  // backgroundColor: (t: any) => t.palette.success.light + "22",
  backgroundColor: (t: any) => t.palette.success.light + "dd",
  fontWeight: 700,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};
const thRight = { ...th, textAlign: "right" };
const thCenter = { ...th, textAlign: "center" };
const td = { ...base };
const tdRight = { ...td, textAlign: "right" };
const tdCenter = { ...td, textAlign: "center" };
const totalCell = {
  ...base,
  fontWeight: 800,
  textAlign: "right",
  backgroundColor: (t: any) => t.palette.grey[100],
};
const totalNum = { ...totalCell, width: 140 };
